import { readonly, type Ref } from 'vue';

import { UserRole } from '~/apiClient';

type UseAuthReturn = ReturnType<typeof useAuth>;
type UseAuthStateReturn = ReturnType<typeof useAuthState>;

type UseAuthUtilsReturn = UseAuthReturn & {
    state: UseAuthStateReturn;
    logout: () => void;
    isLoggedIn: Readonly<Ref<boolean>>;
    isSalesRep: Readonly<Ref<boolean>>;
    isCustomerService: Readonly<Ref<boolean>>;
    companyScopeIds: Readonly<Ref<readonly string[]>>;
};

export const useAuthUtils = (): UseAuthUtilsReturn => {
    const auth = useAuth();
    const authState = useAuthState();
    const { clearToken, data: stateData, token } = useAuthState();

    const logout = (): void => {
        clearToken();
        stateData.value = null;
    };

    const isLoggedIn = computed(() => {
        if (auth.status.value === 'authenticated') return true;
        else if (auth.status.value === 'unauthenticated') return false;

        return auth.status.value === 'loading' && !!token.value;
    });

    const isSalesRep = computed(() => stateData.value?.user?.role === UserRole.SalesRep);

    const isCustomerService = computed(() => stateData.value?.user?.role === UserRole.CustomerService);

    // The companies the user acts on behalf of: their own plus every sibling in the same group.
    // Empty for a guest, which is what makes every sale read as somebody else's.
    //
    // The fallback on the own company is not cosmetic: a session cached before this field existed, or served
    // by an instance not yet deployed, would otherwise answer "no companies" and flip every seller on the
    // site to the buyer view until the session refreshes.
    const companyScopeIds = computed<string[]>(() => {
        const scopeIds = stateData.value?.companyScopeIds;

        if (scopeIds?.length) {
            return scopeIds;
        }

        const companyId = stateData.value?.user?.companyId;

        return companyId ? [companyId] : [];
    });

    return {
        ...auth,
        state: authState,
        logout,
        isLoggedIn: readonly(isLoggedIn),
        isSalesRep: readonly(isSalesRep),
        isCustomerService: readonly(isCustomerService),
        companyScopeIds: readonly(companyScopeIds),
    };
};
